export type ImageLogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_PREFIX = '[Teo Widget IMG]';
const STORAGE_KEY = 'teo_widget_image_debug';

/** Embed / bootstrap fallback. localStorage always wins when set. */
let embedEnabled: boolean | null = null;

function readLocalStorageFlag(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === '1' || stored === 'true') return true;
    if (stored === '0' || stored === 'false') return false;
  } catch {
    // ignore
  }
  return null;
}

function readScriptFlag(): boolean {
  if (typeof document === 'undefined') return false;
  const script = document.querySelector<HTMLScriptElement>(
    'script[data-teo-initialized][data-image-debug], script[data-slug][data-image-debug]',
  );
  return (
    script?.dataset.imageDebug === 'true' || script?.dataset.imageDebug === ''
  );
}

function readDebugFlag(): boolean {
  const stored = readLocalStorageFlag();
  if (stored !== null) return stored;
  if (embedEnabled !== null) return embedEnabled;
  return readScriptFlag();
}

/**
 * Set fallback from embed bootstrap (`data-image-debug` / env).
 * Does **not** write localStorage — live override:
 * `localStorage.setItem('teo_widget_image_debug', '1')`
 */
export function setImageDebugEnabled(enabled: boolean): void {
  embedEnabled = enabled;
}

export function isImageDebugEnabled(): boolean {
  return readDebugFlag();
}

export function shortImageUrl(url?: string): string {
  if (!url) return '(empty)';
  if (url.startsWith('blob:')) return `blob:${url.slice(-12)}`;
  try {
    const parsed = new URL(url);
    return `${parsed.pathname.split('/').pop() ?? url}`;
  } catch {
    return url.length > 48 ? `${url.slice(0, 24)}…${url.slice(-12)}` : url;
  }
}

export function summarizeImageMessage(message: {
  id?: string;
  clientId?: string;
  status?: string;
  mediaUrl?: string;
  attachment?: { url?: string };
}): Record<string, string | undefined> {
  return {
    id: message.id,
    clientId: message.clientId,
    status: message.status,
    url: shortImageUrl(message.mediaUrl ?? message.attachment?.url),
  };
}

export function logImage(
  scope: string,
  message: string,
  data?: unknown,
  level: ImageLogLevel = 'info',
): void {
  if (!readDebugFlag()) return;

  const label = `${LOG_PREFIX} [${scope}] ${message}`;
  const payload = data === undefined ? undefined : [label, data];

  switch (level) {
    case 'debug':
      if (payload) console.debug(...payload);
      else console.debug(label);
      break;
    case 'warn':
      if (payload) console.warn(...payload);
      else console.warn(label);
      break;
    case 'error':
      if (payload) console.error(...payload);
      else console.error(label);
      break;
    default:
      if (payload) console.info(...payload);
      else console.info(label);
  }
}
